import { useNotificationStore } from "@/modules/store/notificationStore";
import { useEffect, useState, useCallback } from "react";
import { notificationService } from "@/modules/services/notificationService";
import { useEmployeeInfo } from "../employee/useEmployeeInfo";
import { AppError } from "@/lib/utils/errors";
// import { enqueueSnackbar } from "notistack";
import { NotificationMessageDto, NotificationObject } from "@/modules/types/notification/notification";

export const useNotification = () => {
  const {
    totalNotification,
    totalUnReadNotification,
    totalReadNotification,
    notifications,
    unReadNotifications,
    readNotifications,
    nextId,
    nextUnReadId,
    nextReadId,
    addNotification,
    addUnReadNotification,
    addReadNotification,
    setNotifications,
    setUnReadNotifications,
    setReadNotifications,
    appendNotifications,
    appendUnReadNotifications,
    appendReadNotifications,
    markAsSeen,
    markAsRead,
    markAllAsRead: markAllAsReadStore,
    clearNotifications,
    clearUnReadNotifications,
    clearReadNotifications,
    setTotalUnReadNotification,
    setTotalNotification,
    setTotalReadNotification,
  } = useNotificationStore();
  const { employeeInfo } = useEmployeeInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRead, setIsLoadingRead] = useState(false);

  // Load initial notifications
  const loadUnreadNotifications = useCallback(async () => {
    if (!employeeInfo?.id) return;

    setIsLoading(true);

    try {
      const response = await notificationService.getNotifications(
        employeeInfo.id,
        1, // page
        20, // limit
        undefined, // isRead
        false, // isSeen
        undefined // nextId
      );

      if (response) {
        setUnReadNotifications(response.notifications, response.nextId);
        setTotalUnReadNotification(response.total);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useNotification.loadUnreadNotifications");
      appErr.setApiAction("notificationService.getNotifications");
      appErr.logError(
        "src/modules/hooks/notification/useNotification.ts[loadUnreadNotifications]"
      );
      // enqueueSnackbar(appErr.message, {
      //   variant: "error",
      // });
    } finally {
      setIsLoading(false);
    }
  }, [employeeInfo?.id]);

  const loadNotifications = useCallback(async () => {
    if (!employeeInfo?.id) return;

    setIsLoading(true);

    try {
      const response = await notificationService.getNotifications(
        employeeInfo.id,
        1, // page
        20, // limit
        undefined, // isRead
        undefined, // isSeen
        undefined // nextId
      );

      if (response) {
        setNotifications(response.notifications, response.nextId);
        setTotalNotification(response.total);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useNotification.loadNotifications");
      appErr.setApiAction("notificationService.getNotifications");
      appErr.logError(
        "src/modules/hooks/notification/useNotification.ts[loadNotifications]"
      );
      // enqueueSnackbar(appErr.message, {
      //   variant: "error",
      // });
    } finally {
      setIsLoading(false);
    }
  }, [employeeInfo?.id]);

  const loadReadNotifications = useCallback(async () => {
    if (!employeeInfo?.id) return;

    setIsLoadingRead(true);

    try {
      const response = await notificationService.getNotifications(
        employeeInfo.id,
        1, // page
        20, // limit
        undefined, // isRead - fetch read notifications
        true, // isSeen - fetch unseen notifications
        undefined // nextId
      );

      if (response) {
        setReadNotifications(response.notifications, response.nextId);
        setTotalReadNotification(response.total);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useNotification.loadReadNotifications");
      appErr.setApiAction("notificationService.getNotifications");
      appErr.logError(
        "src/modules/hooks/notification/useNotification.ts[loadReadNotifications]"
      );
      // enqueueSnackbar(appErr.message, {
      //   variant: "error",
      // });
    } finally {
      setIsLoadingRead(false);
    }
  }, [employeeInfo?.id]);

  const loadMoreNotifications = useCallback(async () => {
    if (!employeeInfo?.id || !nextId) return;

    setIsLoading(true);

    try {
      const response = await notificationService.getNotifications(
        employeeInfo.id,
        undefined, // page
        20, // limit
        undefined, // isRead
        undefined, // isSeen
        nextId
      );

      if (response) {
        appendNotifications(response.notifications, response.nextId);
        setTotalNotification(response.total);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useNotification.loadMoreNotifications");
      appErr.setApiAction("notificationService.getNotifications");
      appErr.logError(
        "src/modules/hooks/notification/useNotification.ts[loadMoreNotifications]"
      );
      // enqueueSnackbar(appErr.message, {
      //   variant: "error",
      // });
    } finally {
      setIsLoading(false);
    }
  }, [employeeInfo?.id, nextId]);

  const loadMoreUnreadNotifications = useCallback(async () => {
    if (!employeeInfo?.id || !nextUnReadId) return;

    setIsLoading(true);

    try {
      const response = await notificationService.getNotifications(
        employeeInfo.id,
        undefined, // page
        20, // limit
        undefined, // isRead
        false, // isSeen
        nextUnReadId
      );

      if (response) {
        appendUnReadNotifications(response.notifications, response.nextId);
        setTotalUnReadNotification(response.total);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useNotification.loadMoreUnreadNotifications");
      appErr.setApiAction("notificationService.getNotifications");
      appErr.logError(
        "src/modules/hooks/notification/useNotification.ts[loadMoreUnreadNotifications]"
      );
      // enqueueSnackbar(appErr.message, {
      //   variant: "error",
      // });
    } finally {
      setIsLoading(false);
    }
  }, [employeeInfo?.id, nextUnReadId]);

  const loadMoreReadNotifications = useCallback(async () => {
    if (!employeeInfo?.id || !nextReadId) return;

    setIsLoadingRead(true);

    try {
      const response = await notificationService.getNotifications(
        employeeInfo.id,
        undefined, // page
        20, // limit
        undefined, // isRead
        true, // isSeen
        nextReadId
      );

      if (response) {
        appendReadNotifications(response.notifications, response.nextId);
        setTotalReadNotification(response.total);
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.setAction("useNotification.loadMoreReadNotifications");
      appErr.setApiAction("notificationService.getNotifications");
      appErr.logError(
        "src/modules/hooks/notification/useNotification.ts[loadMoreReadNotifications]"
      );
      // enqueueSnackbar(appErr.message, {
      //   variant: "error",
      // });
    } finally {
      setIsLoadingRead(false);
    }
  }, [employeeInfo?.id, nextReadId]);

  // Mark notification as seen
  const markNotificationAsSeen = async (notificationId: string) => {
    if (!employeeInfo?.id) return;

    try {
      await notificationService.seenNotification(
        employeeInfo.id,
        notificationId
      );
      markAsSeen(notificationId);
    } catch (err) {
      console.error("Failed to mark notification as seen:", err);
    }
  };

  // Mark notification as read
  const markNotificationAsRead = async (notificationId: string) => {
    if (!employeeInfo?.id) return;

    try {
      await notificationService.seenNotification(
        employeeInfo.id,
        notificationId
      );
      markAsRead(notificationId);
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!employeeInfo?.id) return;

    try {
      await notificationService.markAllAsRead(employeeInfo.id);
      markAllAsReadStore();
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  };

  // Load notifications on mount and when employee info changes
  useEffect(() => {
    if (employeeInfo?.id) {
      clearNotifications();
      clearUnReadNotifications();
      clearReadNotifications();
      loadNotifications();
      loadUnreadNotifications();
      loadReadNotifications();
    }
  }, [
    employeeInfo?.id,
    loadNotifications,
    loadUnreadNotifications,
    loadReadNotifications,
  ]);

  return {
    totalNotification,
    totalUnReadNotification,
    totalReadNotification,
    notifications,
    unReadNotifications,
    readNotifications,
    nextId,
    nextUnReadId,
    nextReadId,
    isLoading,
    isLoadingRead,
    loadNotifications,
    loadUnreadNotifications,
    loadReadNotifications,
    loadMoreNotifications,
    loadMoreUnreadNotifications,
    loadMoreReadNotifications,
    markNotificationAsSeen,
    markNotificationAsRead,
    markAllAsRead,
    addNotification,
    addUnReadNotification,
    addReadNotification,
    clearNotifications,
    clearReadNotifications,
  };
};

export const useNotificationItem = () => {
  const generateContent = (noti: NotificationMessageDto) => {
    const content = noti.content;
    if (!content) return null;

    // Helper to convert byte index to character index
    const byteIndexToCharIndex = (str: string, byteIndex: number) => {
      const buf = new TextEncoder().encode(str);
      return new TextDecoder().decode(buf.slice(0, byteIndex)).length;
    };

    const highlights: { start: number; end: number }[] = [];

    const addHighlight = (obj?: NotificationObject) => {
      if (obj?.metadata && typeof obj.metadata.offset === "number" && typeof obj.metadata.length === "number") {
        const offset = obj.metadata.offset;
        const length = obj.metadata.length;
        if (length > 0) {
          const charStart = byteIndexToCharIndex(content, offset);
          const charEnd = byteIndexToCharIndex(content, offset + length);
          highlights.push({ start: charStart, end: charEnd });
        }
      }
    };

    // Collect all highlights
    if (noti.subject) {
      noti.subject.forEach(addHighlight);
    }
    addHighlight(noti.diObject);
    addHighlight(noti.inObject);
    addHighlight(noti.prObject);

    if (highlights.length === 0) {
      return (
        <p className="font-normal text-sm text-gray-600 dark:text-gray-300">
          {content}
        </p>
      );
    }

    // Sort highlights by start position
    highlights.sort((a, b) => a.start - b.start);

    // Merge overlapping highlights if any (optional safety)
    const merged: { start: number; end: number }[] = [];
    if (highlights.length > 0) {
      let current = highlights[0];
      for (let i = 1; i < highlights.length; i++) {
        if (highlights[i].start < current.end) {
          current.end = Math.max(current.end, highlights[i].end);
        } else {
          merged.push(current);
          current = highlights[i];
        }
      }
      merged.push(current);
    }

    // Build the content components
    const result: React.ReactNode[] = [];
    let lastIndex = 0;

    merged.forEach((h, i) => {
      // Add text before highlight
      if (h.start > lastIndex) {
        result.push(content.substring(lastIndex, h.start));
      }
      // Add highlighted text
      result.push(
        <span
          key={`h-${i}`}
          className="inline font-bold text-sm text-primary dark:text-white"
        >
          {content.substring(h.start, h.end)}
        </span>
      );
      lastIndex = h.end;
    });

    // Add remaining text
    if (lastIndex < content.length) {
      result.push(content.substring(lastIndex));
    }

    return (
      <p className="font-normal text-sm text-gray-600 dark:text-gray-300">
        {result}
      </p>
    );
  };

  return {
    generateContent,
  };
}